import React, { useMemo, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { parse, stringify } from 'qs'
import { MaterialReactTable } from 'material-react-table'
import { MRT_Localization_ES } from 'material-react-table/locales/es'

import { useCombos } from '../../../combos'
import { useIP3HistContext } from '../../contexts'
import { ipHistTableDataPreparation } from '../../logics'
import { IP_HIST_TABLE_COLUMNS, IP_DEFAULT_QUERY } from './ip3-hist-table.constants'
import { ip3HistTableStyles } from './ip3-hist-table.styles'

export const IPHistTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useCombos([])
  const {
    data,
    pagination: { total_registros: totalEntries },
    loading,
    getTableData,
  } = useIP3HistContext()

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })

  useEffect(() => {
    const params = parse(location.search, { ignoreQueryPrefix: true })

    const pageIndex = params._pagina ? Number(params._pagina) - 1 : 0
    const pageSize = params._num_x_pagina ? Number(params._num_x_pagina) : 10

    setPagination({ pageIndex, pageSize })

    if (location.search.substring(1)) {
      getTableData('ip', params)
    } else {
      navigate(`/ip3-historical/ip?${stringify(IP_DEFAULT_QUERY)}`, { replace: true })
    }
  }, [location.search, getTableData, navigate])

  const handlePaginationChange = (updater) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    const currentParams = parse(location.search, { ignoreQueryPrefix: true })

    const newSearch = stringify({
      ...currentParams,
      _pagina: newPagination.pageIndex + 1,
      _num_x_pagina: newPagination.pageSize,
    })

    navigate(`?${newSearch}`)
  }

  const customLocalization = useMemo(
    () => ({
      ...MRT_Localization_ES,
      rowsPerPage: 'Registros',
    }),
    []
  )

  const formattedColumns = useMemo(
    () =>
      IP_HIST_TABLE_COLUMNS.map((column) => ({
        header: formatMessage({ id: `pages.ip3Historical.table.columns.${column.field}.title` }),
        accessorKey: column.field,
        size: column.width,
        muiTableBodyCellProps: { style: { ...ip3HistTableStyles.cell, ...column.cellStyle } },
        Cell: ({ cell }) => {
          const value = cell.getValue()
          if (column.comboId && combos[column.comboId]?.data) {
            const comboData = combos[column.comboId].data
            const foundItem = comboData.find((item) => String(item.key) === String(value))
            return foundItem?.value || <span>--</span>
          }
          return value ?? <span>--</span>
        },
      })),
    [combos, formatMessage]
  )

  const preparedData = useMemo(
    () => ipHistTableDataPreparation({ data, combos, intl }),
    [data, combos, intl]
  )

  return (
    <MaterialReactTable
      columns={formattedColumns}
      data={preparedData || []}
      enableTopToolbar={false}
      enableRowSelection={false}
      enableColumnDragging={false}
      muiTablePaperProps={{ elevation: 0 }}
      muiTableProps={{ sx: { tableLayout: 'fixed' } }}
      muiTableHeadCellProps={{ style: ip3HistTableStyles.header }}
      state={{ isLoading: loading, pagination }}
      manualPagination
      rowCount={totalEntries || 0}
      onPaginationChange={handlePaginationChange}
      localization={customLocalization}
    />
  )
}
